/** Create + start a grid bot. On a LIVE connection this places real orders. */
import { useEffect, useMemo, useRef, useState } from 'react';
import { Button, Card } from '../../components/UI';
import { searchSymbols, type SymbolHit } from '../../lib/dataHooks';
import { breakEvenPct, createBot, getAccount, type AccountView, type BotDefaults, type BotEnv } from '../../lib/bots';
import { CardHead, EnvPill, ErrorLine, Field, fmt, fmtInt, inputStyle, mono } from './ui';

interface Props {
  env: BotEnv;
  defaults: BotDefaults;
  onCreated: (id: string) => void;
  onCancel: () => void;
}

const whole = (s: string) => (/^\d+$/.test(s.trim()) ? Number(s) : NaN);

export const NewBotForm = ({ env, defaults, onCreated, onCancel }: Props) => {
  const [symbol, setSymbol] = useState('');
  const [hit, setHit] = useState<SymbolHit | null>(null);
  const [lotSize, setLotSize] = useState('50');
  const [spreadPct, setSpreadPct] = useState('0.75');
  const [mode, setMode] = useState<'buy' | 'holdings'>('buy');
  const [initialLots, setInitialLots] = useState('6');
  const [maxInventory, setMaxInventory] = useState('');
  const [maxTouched, setMaxTouched] = useState(false);
  const [maxOrders, setMaxOrders] = useState(env === 'live' ? String(defaults.liveMaxOrders) : '');
  const [stopPct, setStopPct] = useState(String(defaults.stopPct));
  const [confirmLive, setConfirmLive] = useState(false);
  const [account, setAccount] = useState<AccountView | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    getAccount().then(setAccount).catch(() => setAccount(null));
  }, []);

  const lot = whole(lotSize);
  const lots = whole(initialLots);
  const held = account?.positions.find((p) => p.symbol === symbol)?.quantity ?? 0;
  const start = mode === 'holdings' ? held : Number.isFinite(lot) && Number.isFinite(lots) ? lot * lots : 0;
  // Pre-filled like the PoC: starting stock + 20 lots of room to buy on the way down.
  const suggestedMax = Number.isFinite(lot) ? start + defaults.extraLots * lot : 0;
  useEffect(() => {
    if (!maxTouched) setMaxInventory(suggestedMax ? String(suggestedMax) : '');
  }, [suggestedMax, maxTouched]);

  const price = hit?.lastPrice ? Number(hit.lastPrice) : null;
  const spread = Number(spreadPct);
  // What a cycle has to clear before it earns anything. Brokerage has a PKR
  // 0.03/share floor, so on cheap shares this is far above the 0.15% headline;
  // the carried figure adds the second brokerage and two CDC minimums.
  const be = price && lot >= 1 ? { sameDay: breakEvenPct(price, lot, true, defaults.tariff), carried: breakEvenPct(price, lot, false, defaults.tariff) } : null;
  
  const problems = useMemo(() => {
    const out: string[] = [];
    if (!symbol) out.push('pick a symbol');
    if (!(lot >= 1)) out.push('shares per order must be a whole number');
    if (!(spread >= 0.05 && spread <= 20)) out.push('spread must be between 0.05% and 20%');
    if (be && spread < be.sameDay) out.push(`spread must be at least ${be.sameDay}% — below that a cycle cannot cover its costs`);
    if (mode === 'buy' && !(lots >= 0)) out.push('initial lots must be a whole number');
    if (mode === 'holdings' && account && held < lot) out.push(`the account holds ${fmtInt(held)} ${symbol || 'shares'} — less than one lot`);
    if (maxInventory && !(whole(maxInventory) >= start)) out.push(`max shares must be at least the starting ${fmtInt(start)}`);
    if (env === 'live' && !(whole(maxOrders) >= 2)) out.push('a live bot needs a daily order cap (2 or more)');
    if (maxOrders && env !== 'live' && !(whole(maxOrders) >= 2)) out.push('order cap must be 2 or more, or blank');
    if (!(Number(stopPct) >= 0.5 && Number(stopPct) <= 50)) out.push('price stop must be between 0.5% and 50%');
    if (env === 'live' && !confirmLive) out.push('confirm that this bot places real orders');
    return out;
  }, [symbol, lot, lots, spread, mode, account, held, maxInventory, start, env, maxOrders, stopPct, confirmLive]);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      const { bot } = await createBot({
        symbol,
        lotSize: lot,
        spreadPct: spread,
        initialLots: mode === 'buy' ? lots : 0,
        useHoldings: mode === 'holdings',
        maxInventory: maxInventory ? whole(maxInventory) : undefined,
        maxOrders: maxOrders ? whole(maxOrders) : undefined,
        stopPct: Number(stopPct),
        confirmLive: env === 'live' ? confirmLive : undefined,
      });
      onCreated(bot.id);
    } catch (e) {
      setError((e as Error).message);
      setBusy(false);
    }
  };

  return (
    <Card padding={24}>
      <CardHead title="New bot" right={<EnvPill env={env} />} />
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))', gap: 12 }}>
          <Field label="Symbol" hint={hit ? `${hit.name}${price ? ` · ${fmt(price)}` : ''}` : undefined}>
            <SymbolInput
              value={symbol}
              onPick={(h) => {
                setSymbol(h.sym);
                setHit(h);
              }}
              onClear={() => {
                setSymbol('');
                setHit(null);
              }}
            />
          </Field>
          <Field label="Shares per order" hint={price && lot >= 1 ? `≈ Rs ${fmt(price * lot, 0)} per order` : undefined}>
            <input style={inputStyle} inputMode="numeric" value={lotSize} onChange={(e) => setLotSize(e.target.value)} />
          </Field>
          <Field
            label="Spread %"
            hint={
              be ? (
                <span style={{ color: spread < be.sameDay ? 'var(--tk-down)' : spread < be.carried ? 'var(--tk-warn)' : 'var(--tk-ink-3)' }}>
                  break-even {be.sameDay}% same-day · {be.carried}% carried
                </span>
              ) : undefined
            }
          >
            <input style={inputStyle} inputMode="decimal" value={spreadPct} onChange={(e) => setSpreadPct(e.target.value)} />
          </Field>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <span style={{ fontSize: 10.5, color: 'var(--tk-ink-3)', letterSpacing: '0.06em', textTransform: 'uppercase', fontWeight: 600, ...mono }}>Starting stock</span>
          <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 13 }}>
            <input type="radio" checked={mode === 'buy'} onChange={() => setMode('buy')} />
            Buy
            <input style={{ ...inputStyle, width: 70 }} inputMode="numeric" value={initialLots} disabled={mode !== 'buy'} onChange={(e) => setInitialLots(e.target.value)} />
            lots at the start{mode === 'buy' && lot >= 1 && lots >= 0 ? ` (${fmtInt(lot * lots)} shares${price ? `, ≈ Rs ${fmt(price * lot * lots, 0)}` : ''})` : ''}
          </label>
          <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 13 }}>
            <input type="radio" checked={mode === 'holdings'} onChange={() => setMode('holdings')} />
            Use the shares already in the account
            {symbol && account ? <span style={{ color: 'var(--tk-ink-3)', ...mono }}>({fmtInt(held)} held)</span> : null}
          </label>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))', gap: 12 }}>
          <Field label="Max shares to hold">
            <input
              style={inputStyle}
              inputMode="numeric"
              value={maxInventory}
              onChange={(e) => {
                setMaxTouched(true);
                setMaxInventory(e.target.value);
              }}
            />
          </Field>
          <Field label="Max orders per day">
            <input style={inputStyle} inputMode="numeric" value={maxOrders} onChange={(e) => setMaxOrders(e.target.value)} />
          </Field>
          <Field label="Price stop %">
            <input style={inputStyle} inputMode="decimal" value={stopPct} onChange={(e) => setStopPct(e.target.value)} />
          </Field>
        </div>


        {env === 'live' && (
          <label style={{ display: 'flex', gap: 10, alignItems: 'flex-start', fontSize: 12.5, color: 'var(--tk-ink-2)', lineHeight: 1.5 }}>
            <input type="checkbox" checked={confirmLive} onChange={(e) => setConfirmLive(e.target.checked)} style={{ marginTop: 3 }} />
            <span>
              This is the <b>LIVE</b> connection. This bot places real orders with real money.
            </span>
          </label>
        )}

        <ErrorLine>{error}</ErrorLine>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
          <Button disabled={busy || problems.length > 0} onClick={submit} variant={env === 'live' ? 'down' : 'primary'}>
            {busy ? 'Starting…' : env === 'live' ? 'Start LIVE bot' : 'Start bot'}
          </Button>
          <Button variant="ghost" onClick={onCancel} disabled={busy}>
            Cancel
          </Button>
          {problems.length > 0 && <span style={{ fontSize: 12, color: 'var(--tk-ink-3)' }}>{problems[0]}</span>}
        </div>
      </div>
    </Card>
  );
};

const SymbolInput = ({ value, onPick, onClear }: { value: string; onPick: (h: SymbolHit) => void; onClear: () => void }) => {
  const [q, setQ] = useState(value);
  const [hits, setHits] = useState<SymbolHit[]>([]);
  const [open, setOpen] = useState(false);
  const seq = useRef(0);

  useEffect(() => {
    const term = q.trim();
    if (!term || term === value) {
      setHits([]);
      return;
    }
    const mine = ++seq.current;
    const timer = setTimeout(() => {
      searchSymbols(term, 8)
        .then((r) => {
          if (mine === seq.current) setHits(r.results.filter((h) => h.market === 'REG' || !h.market));
        })
        .catch(() => undefined);
    }, 150);
    return () => clearTimeout(timer);
  }, [q, value]);

  return (
    <div style={{ position: 'relative' }}>
      <input
        style={inputStyle}
        value={q}
        placeholder="e.g. NBP"
        spellCheck={false}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        onChange={(e) => {
          setQ(e.target.value.toUpperCase());
          setOpen(true);
          if (value) onClear();
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && hits[0]) {
            e.preventDefault();
            setQ(hits[0].sym);
            onPick(hits[0]);
            setOpen(false);
          }
        }}
      />
      {open && hits.length > 0 && (
        <div style={{ position: 'absolute', zIndex: 20, top: '100%', left: 0, right: 0, marginTop: 4, background: 'var(--tk-surface)', border: '1px solid var(--tk-line)', borderRadius: 'var(--tk-radius-sm)', boxShadow: 'var(--tk-shadow-md)', overflow: 'hidden' }}>
          {hits.map((h) => (
            <button
              key={h.sym}
              type="button"
              onMouseDown={(e) => {
                e.preventDefault();
                setQ(h.sym);
                onPick(h);
                setOpen(false);
              }}
              style={{ display: 'flex', width: '100%', gap: 10, alignItems: 'baseline', padding: '8px 11px', background: 'transparent', border: 'none', cursor: 'pointer', textAlign: 'left', color: 'var(--tk-ink)' }}
            >
              <span style={{ fontWeight: 600, fontSize: 13, width: 70, ...mono }}>{h.sym}</span>
              <span style={{ fontSize: 12, color: 'var(--tk-ink-3)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{h.name}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};
